/**
 * The sectors a member's stocks are summed under. A house must name each stock's industry as AMFI
 * classifies it (NSE's industries, about sixty), and those roll up here into fewer, plainer
 * sectors. Banks stand apart from the rest of finance: in an Indian portfolio they are most of it.
 * A stock abroad is summed under its own sector too, where its house names its industry (NASDAQ's,
 * marked `##`): Alphabet's `Internet Content & Information` is IT.
 */

export const SECTORS = [
  'Banks',
  'Financial services',
  'IT',
  'Healthcare',
  'Consumer staples',
  'Consumer discretionary',
  'Autos',
  'Industrials',
  'Energy',
  'Materials',
  'Utilities',
  'Telecom',
  'Realty',
  'Services',
  'Other',
] as const;
export type Sector = (typeof SECTORS)[number];

/** The sectors whose names are plural, said with `are`; the rest take `is`. */
export const PLURAL_SECTORS: ReadonlySet<Sector> = new Set([
  'Banks',
  'Financial services',
  'Consumer staples',
  'Autos',
  'Industrials',
  'Materials',
  'Utilities',
  'Services',
]);

const INDUSTRIES: readonly [RegExp, Sector][] = [
  [/^banks?\b/i, 'Banks'],
  [
    /financ|capital markets|insurance|fintech|financial technology|nbfc|housing finance|credit services|asset management/i,
    'Financial services',
  ],
  [
    /^it\b|software|it services|it - |computers|it enabled|information technology|semiconductor|internet content|interactive media/i,
    'IT',
  ],
  [/pharma|health|hospital|biotech|diagnostic|medical|drug/i, 'Healthcare'],
  [
    /fmcg|food|beverage|personal products|household|tobacco|cigarette|agricultural food|tea|coffee|sugar|edible|discount stores/i,
    'Consumer staples',
  ],
  [/automobile|auto components|tyres|two wheeler|commercial vehicle|^auto/i, 'Autos'],
  [
    /consumer durables|consumer electronics|retail|leisure|hotel|restaurant|textile|apparel|media|entertainment|jewel|footwear|e-commerce|education|other consumer services/i,
    'Consumer discretionary',
  ],
  [
    /aerospace|defen[cs]e|construction$|^construction\b|electrical equipment|industrial|capital goods|engineering|agricultural, commercial|machinery|cables|bearings|heavy electrical/i,
    'Industrials',
  ],
  [/oil|gas|petroleum|consumable fuels|coal|refiner/i, 'Energy'],
  [
    /cement|chemical|fertili[sz]er|agrochemical|metal|steel|alumin|mining|minerals|paper|forest|construction materials|glass|plastic|paints/i,
    'Materials',
  ],
  [/power|utilities|water|electricity/i, 'Utilities'],
  [/telecom/i, 'Telecom'],
  [/realty|real estate/i, 'Realty'],
  [
    /transport|logistics|shipping|aviation|airline|port|commercial services|printing|services/i,
    'Services',
  ],
];

export function sectorOf(industry: string | null): Sector {
  const said = (industry ?? '').replace(/[#^*$]+/g, '').trim();
  if (said === '') return 'Other';
  for (const [pattern, sector] of INDUSTRIES) if (pattern.test(said)) return sector;
  return 'Other';
}
